// chatUnread — ONE reader for "how many messages has this viewer not read in
// this channel", shared by the inbox row badge and the per-lane tab counts.
//
// T035 background. The inbox used to read `channel.state.unreadCount`. That is
// a DERIVED counter stream-chat maintains alongside the real read state, and it
// is suppressed by Channel._countMessageAsUnread() — it never increments while
// the channel is muted, while the viewer's own_capabilities lack "read-events",
// or for shadowed/silent messages. The per-member READ STATE keeps counting in
// exactly those cases, which is why a thread could show its unread divider (and
// per-message read receipts) while the channel-list row showed nothing at all.
//
// The read state is also what stream-chat-react itself trusts: <Channel> seeds
// the in-thread unread divider from `channel.state.read[client.userID]`
// (ChannelInner init → _setChannelUnreadUiState(ownReadState)). So the row and
// the thread now agree by construction, because they read the same field.
//
// `channel.state.unreadCount` is additionally a plain mutable property — a
// React row can never re-render off it. stream-chat-react hands every custom
// ChannelList Preview a live `unread` prop (ChannelPreview keeps it in state,
// debounced, mute-aware, reset on notification.mark_read) for precisely this
// job; callers pass it in as `previewUnread` so its updates drive the repaint.

/**
 * Unread message count for `userId` in `channel`.
 *
 * @param {object} channel   Stream channel (or anything with .state.read).
 * @param {string} userId    The viewer's Stream user id (client.userID).
 * @param {number} [previewUnread] stream-chat-react's live `unread` prop, when
 *                                 the caller is a ChannelList Preview.
 * @returns {number} 0 when nothing is unread or the state is unknown.
 */
export const channelUnreadCount = (channel, userId, previewUnread) => {
  const fromReadState = userId
    ? channel?.state?.read?.[userId]?.unread_messages
    : undefined;

  // The two sources agree in the ordinary case; taking the larger means a
  // suppressed counter can never hide a genuinely unread row, and neither
  // source can invent unread messages the other has already cleared to 0
  // (both are zeroed by the same message.read / notification.mark_read).
  return Math.max(
    typeof previewUnread === "number" && previewUnread > 0 ? previewUnread : 0,
    typeof fromReadState === "number" && fromReadState > 0 ? fromReadState : 0,
  );
};

export default channelUnreadCount;
